<template>
  <div class="child-avatar"
       :class="classes"
       @click="avatarClicked">
    <div class="picture"
         :style="avatarStyles"
         :class="avatarClasses"></div>

    <div class="settings-button"
         v-if="showSettings">
      <icon-settings width="11"
                     height="11" />
    </div>

    <div class="icon"
         :style="iconStyles"
         v-if="icon">
      <component :is="icon"
                 width="10"
                 height="10" />
    </div>

    <div class="out-of-sync-warning"
         :style="outOfSyncIconStyles"
         v-if="showOutOfSync && outOfSync">
      <icon-exclamation-sign width="10"
                             height="10" />
    </div>

    <div class="custom-icon"
         :style="customIconStyles"
         v-if="customIconUrl"></div>
  </div>
</template>

<script>
  import predefinedAvatars from '@/constants/predefinedAvatars.js'
  import analytics from '@/services/analytics.js'

  export default {
    props: {
      child: {
        required: false,
        type: Object
      },

      size: {
        required: false,
        type: String,
        default: 'normal'
      },

      showStatus: {
        required: false,
        default: true
      },

      showSettings: {
        required: false,
        type: Boolean,
        default: false
      },

      icon: {
        required: false,
        type: String
      },

      iconColor: {
        required: false,
        type: String,
        default: '#878787'
      },

      iconBackgroundColor: {
        required: false,
        type: String,
        default: '#fff'
      },

      customIconUrl: {
        required: false,
        type: String
      },

      showOutOfSync: {
        required: false,
        type: Boolean,
        default: false
      },

      outOfSyncPosition: {
        required: false,
        type: String,
        default: 'right'
      }
    },

    data() {
      return {
        timerId: null
      }
    },

    created() {
      this.initStatusTimer()
    },

    computed: {
      status() {
        return this.child ? this.child.status.active : ''
      },

      classes() {
        return [
          `size-${this.size}`,
          { 'has-border': this.status && this.showStatus },
          { 'border-green': this.status && this.showStatus }
        ]
      },

      avatarStyles() {
        let image

        if (!this.child) {
          image = predefinedAvatars['deleted_user']
        } else {
          image = this.child.avatar_download_url

          for (let key in predefinedAvatars) {
            if (this.child.avatar_id === key) {
              image = predefinedAvatars[key]
            }
          }
        }

        return {
          'background-image': 'url(' + image + ')',
          'background-size': 'cover',
          'background-position': 'center'
        }
      },

      avatarClasses() {
        let classes = []

        let pale = this.child ? this.child.devices.length === 0 || !this.currentUser.hasPaidLicense : false

        if (pale) {
          classes.push('pale')
        }

        return classes
      },

      iconStyles() {
        return {
          'background-color': this.iconBackgroundColor,
          'color': this.iconColor
        }
      },

      outOfSyncIconStyles() {
        let position

        switch (this.outOfSyncPosition) {
          case 'left':
            position = {
              'left': '3px',
              'bottom': '0'
            }
            break;
        
          default:
            position = {
              'bottom': '-2px',
              'right': '-2px'
            }
        }

        return position
      },

      customIconStyles() {
        return {
          'background-image': 'url(' + this.customIconUrl + ')',
          'background-size': 'cover',
          'background-position': 'center'
        }
      },

      outOfSync() {
        return this.child ? this.child.status.devices_missing > 0 : false
      },

      ...mapGetters('auth', [
        'currentUser'
      ])
    },

    methods: {
      avatarClicked() {
        this.$emit('click')
        analytics.sendEvent('Pk_Child')
      },

      initStatusTimer() {
        if (this.timerId) {
          clearTimeout(this.timerId)
        }

        if (this.status) {
          this.timerId = setTimeout(() => {
            // set child.status.active to 'false' after 10 min inactivity
            this.$store.dispatch('children/updateChildStatus', {
              child_id: this.child.id,
              active: false
            })
          }, 1000 * 60 * 10)
        }
      }
    },

    watch: {
      status(active) {
        if (active) {
          this.initStatusTimer()
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .child-avatar {
    position: relative;
    cursor: pointer;

    &.size-small {
      width: 30px;
      height: 30px;
    }

    &.size-normal {
      width: 48px;
      height: 48px;
    }

    &.size-large {
      width: 78px;
      height: 78px;
    }

    &.has-border {
      border-radius: 50%;
      padding: 2px;
      border: 3px solid transparent;

      &.size-small {
        border-width: 2px;
      }
    }

    &.border-transparent {
      border-color: transparent;
    }

    &.border-green {
      border-color: #2EE98B;
    }

    &.border-red {
      border-color: #F35D5D;
    }

    .picture, .custom-icon {
      width: 100%;
      height: 100%;
      border-radius: 50%;
    }

    .picture {
      background-color: #f1f0f9;

      &.pale {
        opacity: 0.5;
      }
    }

    .icon, .custom-icon {
      position: absolute;
      bottom: -2px;
      right: -2px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 16px;
      height: 16px;
      box-shadow: 0 2px 4px 0 rgba(#000, .1);
      border-radius: 50%;
    }

    .out-of-sync-warning {
      background-color: #F35D5D;
      color: #fff;
      position: absolute;
      bottom: -2px;
      right: -2px;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 16px;
      height: 16px;
      box-shadow: 0 2px 4px 0 rgba(#000, .1);
      border-radius: 50%;
    }

    .custom-icon {
      background-color: #fff;
    }

    .settings-button {
      position: absolute;
      bottom: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 17px;
      height: 17px;
      color: map.get($colors, primary);
      background-color: #fff;
      box-shadow: 0 2px 4px 0 rgba(#000, .1);
      border-radius: 50%;
      cursor: pointer;
    }
  }
</style>
